import React, {FC} from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

/** Supported view modes for the vacation parameters section. */
export type VacationView = 'palowan' | 'country';

export interface VacationViewToggleProps {
  /** Currently selected view. */
  selectedView: VacationView;
  /** Callback invoked when the user selects a different view. */
  onViewChange: (view: VacationView) => void;
}

/**
 * Dropdown that lets the admin toggle between the "Por Palowan" (per-person overrides)
 * and "Por País" (country-level rules) vacation parameter views.
 *
 * @param selectedView - The currently active view identifier.
 * @param onViewChange - Handler called with the newly selected view.
 * @returns A labelled MUI Select control with two options.
 */
const VacationViewToggle: FC<VacationViewToggleProps> = ({
  selectedView,
  onViewChange,
}) => {
  const {t} = useTranslation();

  const handleChange = (event: SelectChangeEvent<VacationView>) => {
    onViewChange(event.target.value as VacationView);
  };

  return (
    <FormControl sx={{minWidth: 200}}>
      <Typography variant="subtitle2" sx={{mb: 0.5, paddingTop: 2}}>
        {t('leaves.VacationViewToggle.label')}
      </Typography>
      <Select<VacationView>
        labelId="vacation-view-toggle-label"
        id="vacation-view-toggle"
        value={selectedView}
        onChange={handleChange}
        inputProps={{'aria-label': 'Seleccionar vista de vacaciones'}}
        MenuProps={{disableRestoreFocus: true}}
      >
        <MenuItem value="palowan">
          {t('leaves.VacationViewToggle.option.palowan')}
        </MenuItem>
        <MenuItem value="country">
          {t('leaves.VacationViewToggle.option.country')}
        </MenuItem>
      </Select>
    </FormControl>
  );
};

export default VacationViewToggle;
